/**
 * Auswertung des Ergebnisses von `kaufland.update_units`.
 *
 * Warum eigens: Der Job wird `completed`, sobald er durchgelaufen ist — auch wenn Kaufland
 * die einzelne Änderung abgelehnt hat. Wer nur `status === 'completed'` prüft, lässt einen
 * Preis in der Liste stehen, den Kaufland nie angenommen hat. Das Urteil steht eine Ebene
 * tiefer, in `result.results[]` pro `idUnit`.
 *
 * Reine Funktion, damit die Fallunterscheidung testbar bleibt.
 */

/**
 * Gegenprobe des Workers bei Kaufland nach einer Preisänderung.
 *
 * `confirmed` — Kaufland liefert den neuen Preis beim Lesen zurück.
 * `mismatch`  — Kaufland liefert weiter einen anderen Wert.
 * `unavailable` — wir konnten nicht lesen; damit wissen wir nichts.
 * `undefined` — die Änderung enthielt keinen Preis, oder der Worker ist älter.
 */
export type PriceConfirmation = 'confirmed' | 'mismatch' | 'unavailable';

export interface UnitUpdateOutcome {
  /** Hat Kaufland die Änderung für diese Einheit angenommen? */
  ok: boolean;
  /** Fehlertext des Marktplatzes, falls vorhanden. */
  error?: string;
  /** Ergebnis der Rückfrage bei Kaufland — nur bei Preisänderungen gesetzt. */
  priceConfirmed?: PriceConfirmation;
  /**
   * Kaufland hat angenommen, aber das Zurückschreiben in den Vario-Verkaufskanal ist
   * gescheitert. Kein Grund zum Zurücksetzen — der Kaufland-Push ist bereits draußen —
   * aber der Nutzer muss es erfahren, weil `kaufland-price-sync-15m` den Preis sonst
   * beim nächsten Lauf aus Vario zurückdreht.
   */
  varioPriceFailed: boolean;
}

interface ResultEntry {
  idUnit?: unknown;
  ok?: unknown;
  error?: unknown;
  priceConfirmed?: unknown;
}

const CONFIRMATIONS: readonly string[] = ['confirmed', 'mismatch', 'unavailable'];

function asConfirmation(v: unknown): PriceConfirmation | undefined {
  return typeof v === 'string' && CONFIRMATIONS.includes(v) ? (v as PriceConfirmation) : undefined;
}

export function readUnitUpdateOutcome(result: unknown, idUnit: string): UnitUpdateOutcome {
  const root = isRecord(result) ? result : null;
  const varioPrice = root && isRecord(root['varioPrice']) ? root['varioPrice'] : null;
  const failures = varioPrice ? varioPrice['failures'] : null;
  const varioPriceFailed = typeof failures === 'number' && failures > 0;

  const rows = root?.['results'];
  // Kein `results`-Feld: älterer Worker oder unerwartete Form. Dann liegt kein Urteil vor,
  // und wir erfinden keines — der Job selbst galt ja als erfolgreich.
  if (!Array.isArray(rows)) return { ok: true, varioPriceFailed };

  const entry = (rows as ResultEntry[]).find(
    (r) => isRecord(r) && String(r.idUnit ?? '') === String(idUnit),
  );
  if (!entry) {
    return {
      ok: false,
      error: 'Der Worker hat zu diesem Angebot nichts zurückgemeldet.',
      varioPriceFailed,
    };
  }
  const confirmation = asConfirmation(entry.priceConfirmed);
  if (entry.ok === true) {
    return {
      ok: true,
      ...(confirmation ? { priceConfirmed: confirmation } : {}),
      varioPriceFailed,
    };
  }
  return {
    ok: false,
    ...(typeof entry.error === 'string' && entry.error ? { error: entry.error } : {}),
    ...(confirmation ? { priceConfirmed: confirmation } : {}),
    varioPriceFailed,
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}
